// Owner BI — which extras sell best + extra revenue per month (from upsell requests; approved = sold).
import React, { useEffect, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { apiCall } from './_shared';

const eur = (n) => `€${Math.round(Number(n) || 0).toLocaleString()}`;
const monthLabel = (k) => new Date(`${k}-01T00:00:00`).toLocaleDateString(undefined, { month: 'short', year: '2-digit' });

const Kpi = ({ label, value, sub, cls = 'bg-white', testId }) => (
  <div className={`${cls} border rounded-xl p-3`}>
    <div className="text-[10px] uppercase text-slate-500 font-semibold">{label}</div>
    <div className="text-2xl font-bold text-slate-900" data-testid={testId}>{value}</div>
    {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
  </div>
);

const BestSellers = ({ items }) => {
  const max = Math.max(1, ...items.map((i) => i.revenue_eur));
  if (!items.length) return <div className="text-xs text-slate-400" data-testid="upsell-best-empty">No extras requested in this period yet.</div>;
  return (
    <div className="space-y-1.5" data-testid="upsell-best-sellers">
      {items.map((i, idx) => (
        <div key={i.key} className="flex items-center gap-2 text-xs" data-testid={`upsell-item-${i.key}`}>
          <span className="w-4 text-slate-400 font-bold">{idx + 1}</span>
          <span className="w-44 truncate font-semibold text-slate-800" title={i.name}>{i.name}</span>
          <div className="flex-1 h-4 bg-slate-100 rounded overflow-hidden"><div className="h-full bg-orange-400 rounded" style={{ width: `${(i.revenue_eur / max) * 100}%` }} /></div>
          <span className="w-16 text-right font-bold text-orange-700">{eur(i.revenue_eur)}</span>
          <span className="w-24 text-right text-slate-500">{i.approved}/{i.requested} · {i.approval_pct}%</span>
        </div>
      ))}
    </div>
  );
};

const UpsellStatsCard = () => {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiCall(`/api/admin/upsells/stats?months=${months}`).then((d) => alive && setData(d)).catch(() => {}).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [months]);

  const t = data?.totals || {};
  const list = data?.months || [];
  const rows = list.slice().reverse().map((m) => (
    <tr key={m.month} className="border-b last:border-0" data-testid={`upsell-row-${m.month}`}>
      <td className="py-1.5 px-2 font-semibold">{monthLabel(m.month)}</td>
      <td className="py-1.5 px-2 text-right">{m.requests}</td>
      <td className="py-1.5 px-2 text-right text-emerald-700 font-semibold">{m.approved}</td>
      <td className="py-1.5 px-2 text-right text-slate-500">{m.declined} / {m.pending}</td>
      <td className="py-1.5 px-2 text-right text-orange-700 font-bold">{eur(m.revenue_eur)}</td>
    </tr>
  ));

  return (
    <div className="bg-white border rounded-xl p-4 space-y-3" data-testid="upsell-stats-card">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-bold text-slate-900 flex items-center gap-2"><Sparkles className="w-4 h-4 text-orange-500" /> Extras that sell</h3>
        <span className="text-xs text-slate-500">Requested from the online check-in and the pre-arrival e-mails · approved by reception = sold.</span>
        <span className="flex-1" />
        {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        <select value={months} onChange={(e) => setMonths(Number(e.target.value))} className="border rounded-lg px-2 py-1 text-xs" data-testid="upsell-months">
          <option value={3}>3 months</option><option value={6}>6 months</option><option value={12}>12 months</option>
        </select>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Extra revenue" value={eur(t.revenue_eur)} sub={`≈ ${eur(t.per_month_eur)} per month`} cls="bg-orange-50 border-orange-200" testId="upsell-kpi-revenue" />
        <Kpi label="Requests" value={t.requests ?? '—'} sub={`${t.pending ?? 0} waiting for reception`} testId="upsell-kpi-requests" />
        <Kpi label="Approval rate" value={`${t.approval_pct ?? 0}%`} sub={`${t.approved ?? 0} approved · ${t.declined ?? 0} declined`} cls="bg-emerald-50 border-emerald-200" testId="upsell-kpi-approval" />
        <Kpi label="Avg. per sale" value={eur(t.avg_ticket_eur)} sub="per approved request" testId="upsell-kpi-avg" />
      </div>
      <div>
        <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1.5">Best sellers · revenue · approved/requested</div>
        <BestSellers items={data?.items || []} />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500"><tr><th className="text-left py-1.5 px-2">Month</th><th className="text-right py-1.5 px-2">Requests</th><th className="text-right py-1.5 px-2">Approved</th><th className="text-right py-1.5 px-2">Declined / open</th><th className="text-right py-1.5 px-2">Extra revenue</th></tr></thead>
          <tbody>{rows}</tbody>
        </table>
      </div>
    </div>
  );
};

export default UpsellStatsCard;
